import { useEffect } from 'react';
import type { PageBreadcrumbItem } from '@/components/shared';
import { useAuthStore } from '@/stores/auth-store';
import { useIntelligenceViewStore } from '@/stores/intelligence-view-store';
import {
  audienceBackPath,
  audienceCrumbLabel,
  viewFor,
  type IntelligenceView,
} from '../lib/intelligence-view';
import { useSavedAudiencesQuery } from './use-saved-audiences';

/** The signed-in user's last Intelligence view, if any. */
export const useIntelligenceView = (): IntelligenceView | undefined => {
  const userId = useAuthStore((s) => s.user?.id);
  const view = useIntelligenceViewStore((s) => s.view);
  return viewFor(view, userId);
};

/**
 * A profile's first crumb: the audience's name, leading back to it (NPJB-828).
 *
 * There is no single-audience read, so the name comes from the saved-audience list. The
 * audience page reads that list too, so arriving from there it is already cached. An
 * internal user's list holds every client's audiences, so no client needs to be picked. It
 * carries a name, so it truncates like the other named crumbs (NPJB-636).
 */
export const useAudienceCrumb = (audienceId: string): PageBreadcrumbItem & { to: string } => {
  const view = useIntelligenceView();
  const { audiences } = useSavedAudiencesQuery();
  return {
    label: audienceCrumbLabel(audiences, audienceId),
    to: audienceBackPath(audienceId, view),
    truncate: true,
  };
};

/**
 * The audience page records what it has open — the audience, or for an internal user at least
 * the client — so the way back can return to it.
 */
export const useRememberIntelligenceView = (
  audienceId: string | undefined,
  tenantId: string | undefined,
) => {
  const userId = useAuthStore((s) => s.user?.id);
  const remember = useIntelligenceViewStore((s) => s.remember);
  useEffect(() => {
    if (userId && (audienceId || tenantId)) remember({ userId, audienceId, tenantId });
  }, [userId, audienceId, tenantId, remember]);
};
